import { useMutation, useQuery } from '@apollo/client';
import { message } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { accountsClient } from '../../../utils/accountsClient';
import { GET_USER_DATA } from '../../../utils/queries';
import { UPDATE_USER_TOUR_STATUS } from '../graphql/queries';
import { TakeTourContext } from './TakeTourContext';

const defaultTourState = {
  isOpen: false,
  currentStep: null,
  tourType: null, // TAKE_TOUR_TYPES
  steps: [],
  data: {},
};

export const TakeTourProvider = ({ children }) => {
  const [tourTakenForSession, setTourTakenForSession] = useState({});
  const [tourState, setTourState] = useState(defaultTourState);
  const [dropdownAndMiscStates, setDropdownAndMiscStates] = useState({});
  const navigate = useNavigate();

  // GraphQL queries and mutations
  const {
    data: tourStatusesData,
    loading: tourStatusesLoading,
    refetch,
  } = useQuery(GET_USER_DATA, {
    client: accountsClient,
    errorPolicy: 'all',
    onError: () => {
      message.error('Failed to fetch tour statuses');
    },
  });

  const [updateUserTourStatus, { loading: updateUserTourStatusLoading }] = useMutation(
    UPDATE_USER_TOUR_STATUS,
    {
      client: accountsClient,
      onCompleted: () => {
        refetch();
      },
      onError: () => {
        message.error('Failed to update tour status');
      },
      // update: (cache, { data }) => {
      // cache.writeQuery({
      //   query: GET_USER_DATA,
      //   data: {
      //     ...tourStatusesData.getUserById,
      //     contractUserSetting: {
      //       ...tourStatusesData.getUserById.contractUserSetting,
      //       tourStatuses: [
      //         {
      //           created: '2026-03-10T06:16:17.889630+00:00',
      //           hasTakenTour: true,
      //           id: Date.now(),
      //           modified: '2026-03-10T06:16:17.889650+00:00',
      //           reminderDate: '2099-01-01',
      //           service: TAKE_TOUR_TYPES.DOCCHAT_HOMEPAGE,
      //         },
      //       ],
      //     },
      //   },
      // });
      // },
    }
  );

  // Helper function to update tour status
  const updateTourStatus = useCallback(
    async (operation, product) => {
      try {
        // Ideally we should update the apollo client cache but cache update isn't working.
        // I guess because of missing apollo client Provider, caching mechanism isn't working properly
        setTourTakenForSession((prev) => ({
          ...prev,
          [product]: true,
        }));

        await updateUserTourStatus({
          variables: {
            operation,
            product,
          },
        });
      } catch (error) {
        console.error('Error updating tour status:', error);
      }
    },
    [updateUserTourStatus]
  );

  const startTour = useCallback((tourType, steps, data = {}) => {
    setTourState((prev) => {
      // If tour is already open, don't start a new one before calling closeTour
      if (prev.isOpen) return prev;
      return {
        isOpen: true,
        currentStep: 0,
        tourType,
        steps,
        data: {
          manualTrigger: data.manualTrigger || false,
          ...data,
        },
      };
    });
  }, []);

  const [stepInProgressState, setStepInProgress] = useState(false);

  // the overlay of take tour will change opacity smoothly. this is added to avoid flickering
  const stepInProgress = useCallback(() => {
    setStepInProgress(true);
    setTimeout(() => {
      setStepInProgress(false);
    }, 500);
  }, []);

  // Helper function to get tour status for a specific service
  const getTourStatus = useCallback(
    (service) => {
      const tourStatuses = tourStatusesData?.getUserById?.contractUserSetting?.tourStatuses || [];
      return tourStatuses.find((status) => status.service === service);
    },
    [tourStatusesData]
  );

  // Helper function to check if user has taken tour for a specific service
  const hasUserTakenTour = useCallback(
    (service) => {
      const tourStatus = getTourStatus(service);
      return tourStatus?.hasTakenTour || false;
    },
    [getTourStatus]
  );

  const closeTour = useCallback(
    (type) => {
      if (!hasUserTakenTour(tourState.tourType)) {
        updateTourStatus(type, tourState.tourType);
      }

      // set the original url back on which the user clicked take tour
      navigate(tourState.data.currentUrl);
      setTourState(defaultTourState);
      setDropdownAndMiscStates({});
    },
    [tourState.tourType, hasUserTakenTour, navigate, tourState.data.currentUrl, updateTourStatus]
  );

  const nextStep = useCallback(() => {
    stepInProgress();
    setTourState((prev) => {
      if (prev.currentStep < prev.steps.length - 1) {
        return { ...prev, currentStep: prev.currentStep + 1 };
      }
      return { ...prev, isOpen: false };
    });
  }, [stepInProgress]);

  const prevStep = useCallback(() => {
    stepInProgress();
    setTourState((prev) => {
      if (prev.currentStep > 0) {
        return { ...prev, currentStep: prev.currentStep - 1 };
      }
      return prev;
    });
  }, [stepInProgress]);

  const value = useMemo(
    () => ({
      ...tourState,
      dropdownAndMiscStates,
      setDropdownAndMiscStates,
      startTour,
      nextStep,
      prevStep,
      closeTour,
      stepInProgress,
      stepInProgressState,
      // GraphQL related functions
      tourStatusesData,
      tourStatusesLoading,
      updateUserTourStatusLoading,
      getTourStatus,
      hasUserTakenTour,
      tourTakenForSession,
    }),
    [
      tourState,
      startTour,
      nextStep,
      prevStep,
      closeTour,
      stepInProgress,
      stepInProgressState,
      dropdownAndMiscStates,
      setDropdownAndMiscStates,
      tourStatusesData,
      tourStatusesLoading,
      updateUserTourStatusLoading,
      getTourStatus,
      hasUserTakenTour,
      tourTakenForSession,
    ]
  );

  return <TakeTourContext.Provider value={value}>{children}</TakeTourContext.Provider>;
};
